import type { OrderSummaryResponse } from '@thinkalpha/locates-ws-client/contracts/orders.js';
import type { RevenueAnalyticsRawRow } from 'src/admin/LAB/RevenueAnalytics/types';
import { lapi as axios } from 'src/api';

export interface GetRevenueAnalyticsOptions {
    /**
     * Limit the rows to a single provider. Omitted (or empty) loads the
     * aggregate across every provider the caller can see.
     */
    providerId?: string;
    /** Zero-based starting row. Omitted (with `limit`) when searching a symbol. */
    skip?: number;
    /** Page size. Omitted (with `skip`) when searching a symbol. */
    limit?: number;
    /**
     * Exact-match symbol filter. When set, the backend scopes the result
     * to that symbol and ignores `skip`/`limit`.
     */
    symbol?: string;
}

/**
 * Fetch one page of per-symbol revenue analytics and map the response
 * onto the table's raw row shape. Each row keeps its `providerId`; the
 * display name is resolved later against the loaded providers list, since
 * the aggregate view spans providers.
 *
 * Served by `GET /v1/orders/summary/by-symbol`; `providerId` narrows the
 * rows to one provider (omitted loads all providers) and `skip`/`limit`
 * page them. `inventory` is optional per row, so the SOD figures are left
 * `null` when the backend has no start-of-day data (the table renders
 * those as a dash).
 */
export const getRevenueAnalytics = async (
    options: GetRevenueAnalyticsOptions = {},
): Promise<RevenueAnalyticsRawRow[]> => {
    const { providerId, skip, limit, symbol } = options;
    const { data } = await axios.get<OrderSummaryResponse>('/v1/orders/summary/by-symbol', {
        params: { providerId: providerId || undefined, skip, limit, symbol },
    });
    return data.rows.map((row) => ({
        providerId: row.providerId,
        symbol: row.symbol,
        qty: row.quantity,
        avgPrice: row.averagePrice,
        totalCost: row.totalCost,
        curPrice: row.currentPrice,
        fairValue: row.fairValue,
        sodInventory: row.inventory?.startOfDay ?? null,
        remainingInventory: row.inventory?.remaining ?? null,
    }));
};
